import { type DiscountTier, discountsFor, lineAmount, parseQty, r3, settingNum } from '@sj/shared';
import { useEffect, useReducer, useRef } from 'react';
import { useSession } from '../../app/session.js';
import { BankSelect, ChoiceSelect } from '../../components/Choice.js';
import { Icon } from '../../components/Icon.js';
import { PhotoInput } from '../../components/PhotoInput.js';
import { addDays, num, qtyInput, rp, today } from '../../lib/format.js';
import { label } from '../../lib/status.js';
import type { Nota, NotaItem } from './types.js';

// Isi modal serah terima (port A.deliverModal v1 di ops.js): checklist turun barang, qty diterima, cara bayar & foto.

export interface Got {
  id: number;
  qty: number;
  rec: number;
  price: number;
  ok: boolean;
  name: string;
  unit: string;
  division: string;
}

/** Status modal yang dibaca saat simpan (v1: variabel got / method / cashTouched / tfTouched). */
export interface DeliverState {
  got: Got[];
  method: string;
  cashTouched: boolean;
  tfTouched: boolean;
}

export const newDeliverState = (n: Nota, items: NotaItem[]): DeliverState => ({
  got: items.map((i) => ({
    id: i.id,
    qty: i.qty,
    rec: i.qty,
    price: i.price,
    ok: false,
    name: i.name,
    unit: i.unit,
    division: i.division,
  })),
  method: n.pay_method,
  cashTouched: false,
  tfTouched: false,
});

/** Total tagihan dari barang yang benar-benar diterima (diskon skema ikut qty diterima, sama dengan server). */
export const deliverTotal = (got: Got[], tiers: DiscountTier[]): number => {
  const ds = discountsFor(
    got.map((g) => ({ division: g.division, unit: g.unit, price: g.price, qty: g.rec })),
    tiers,
  );
  return got.reduce((a, g, i) => a + lineAmount(g.rec, g.price, ds[i]), 0);
};

export function DeliverBody({
  n,
  items,
  st,
  tiers,
  drv,
  photos,
}: {
  n: Nota;
  items: NotaItem[];
  st: DeliverState;
  tiers: DiscountTier[];
  drv: boolean;
  photos: { current: Record<string, string> };
}) {
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const { meta } = useSession();
  const recRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cashRef = useRef<HTMLInputElement>(null);
  const tfRef = useRef<HTMLInputElement>(null);
  const { got } = st;
  const total = deliverTotal(got, tiers);
  // Titipan (dibayar sebelum kirim) mengurangi yang ditagih sekarang.
  const titipan = n.paid || 0;
  const due = Math.max(0, total - titipan);
  const pays = ['cash', 'transfer', ...(!drv || n.pay_method === 'tempo' || n.term_days ? ['tempo'] : [])];
  const done = got.filter((g) => g.ok).length;
  const anyShort = got.some((g) => g.rec < g.qty);

  // v1 upd(): nominal cash/transfer mengikuti total selama belum diubah tangan.
  useEffect(() => {
    if (!st.cashTouched && cashRef.current) cashRef.current.value = String(due);
    if (!st.tfTouched && tfRef.current) tfRef.current.value = String(due);
  });

  const step = (idx: number, d: number) => {
    const g = got[idx] as Got;
    g.rec = Math.max(0, Math.min(g.qty, r3(g.rec + d)));
    const el = recRefs.current[idx];
    if (el) el.value = qtyInput(g.rec);
    bump();
  };

  return (
    <>
      <div className="small muted">{`${n.store_name} · ${n.address || ''}`}</div>
      <div className="row between">
        <b>Checklist turun barang</b>
        <span className={`pill ${done === got.length ? 't-ok' : 't-warn'}`} id="dChk">
          {`${done}/${got.length} diturunkan`}
        </span>
      </div>
      <div className="lines">
        {items.map((i, idx) => {
          const g = got[idx] as Got;
          const short = g.rec < g.qty;
          return (
            <div key={i.id} className={`line uc${g.ok ? ' done' : ''}${short ? ' short' : ''}`} data-row={idx}>
              <button
                type="button"
                className="lc-box"
                data-m="chk"
                data-i={idx}
                aria-label={`Centang ${i.name} sudah diturunkan`}
                onClick={() => {
                  g.ok = !g.ok;
                  bump();
                }}
              >
                <Icon n="check" cls="i-sm" />
              </button>
              <div className="body">
                <b>{i.name}</b>
                <span>{`di nota ${num(i.qty)} ${i.unit} · ${rp(i.price)}`}</span>
                <em className="uc-st" data-st={idx}>
                  {g.ok
                    ? short
                      ? `✓ diturunkan ${num(g.rec)} — kurang ${num(r3(g.qty - g.rec))} ${g.unit}`
                      : `✓ diturunkan ${num(g.rec)} ${g.unit}`
                    : short
                      ? `kurang ${num(r3(g.qty - g.rec))} ${g.unit}`
                      : ''}
                </em>
              </div>
              <span className="stepper">
                <button type="button" data-m="step" data-i={idx} data-d="-1" onClick={() => step(idx, -1)}>
                  −
                </button>
                <input
                  autoComplete="off"
                  inputMode="decimal"
                  data-rec={idx}
                  defaultValue={qtyInput(i.qty)}
                  ref={(el) => {
                    recRefs.current[idx] = el;
                  }}
                  onChange={(e) => {
                    const q = parseQty(e.currentTarget.value);
                    g.rec = Math.max(0, Math.min(g.qty, Number.isFinite(q) ? q : 0));
                    bump();
                  }}
                />
                <button type="button" data-m="step" data-i={idx} data-d="1" onClick={() => step(idx, 1)}>
                  +
                </button>
              </span>
            </div>
          );
        })}
      </div>
      <div className="small muted">
        Hitung &amp; centang setiap barang yang diturunkan. Tidak bisa melebihi muatan nota; bila kurang (ditolak/rusak) pilih
        alasannya — sisa dibawa pulang &amp; masuk stok saat nota kembali.
      </div>
      <ChoiceSelect
        kind="short_reason"
        name="short_note"
        empty="— pilih alasan —"
        label="Alasan kurang kirim"
        cls={anyShort ? '' : 'hidden'}
        wrapId="dShort"
      />
      <div className="total-box">
        <span>Total tagihan</span>
        <span id="dTotal">{rp(total)}</span>
      </div>
      {titipan > 0 ? (
        <div className="note info small" id="dTitipan">
          {`Titipan sudah dibayar ${rp(titipan)} — ditagih sekarang `}
          <b>{rp(due)}</b>.
        </div>
      ) : null}
      <label className="f">
        Nama penerima
        <input
          autoComplete="off"
          className="inp"
          name="receiver_name"
          required
          maxLength={80}
          placeholder="nama penerima di toko"
        />
      </label>
      <div className="f">
        <span className="small b muted">Pembayaran</span>
        <div className="seg" id="dPay">
          {pays.map((k) => (
            <button
              key={k}
              type="button"
              data-m="pm"
              data-k={k}
              className={k === st.method ? 'on' : ''}
              onClick={() => {
                st.method = k;
                bump();
              }}
            >
              {label('pm', k)}
            </button>
          ))}
        </div>
      </div>
      <div id="pmCash" className={`stack-sm${st.method === 'cash' ? '' : ' hidden'}`}>
        <label className="f">
          {'Uang cash diterima '}
          <span className="hint">Boleh kurang dari tagihan — sisanya jadi piutang.</span>
          <input
            autoComplete="off"
            ref={cashRef}
            className="inp amt"
            type="number"
            name="cash_amount"
            min="0"
            defaultValue={due}
            onInput={() => {
              st.cashTouched = true;
            }}
          />
        </label>
      </div>
      <div id="pmTransfer" className={`stack-sm${st.method === 'transfer' ? '' : ' hidden'}`}>
        <div className="grid2">
          <label className="f">
            Nominal transfer
            <input
              autoComplete="off"
              ref={tfRef}
              className="inp amt"
              type="number"
              name="transfer_amount"
              min="0"
              defaultValue={due}
              onInput={() => {
                st.tfTouched = true;
              }}
            />
          </label>
          <label className="f">
            No. ref / bank pengirim
            <input autoComplete="off" className="inp" name="transfer_ref" maxLength={80} />
          </label>
        </div>
        <BankSelect name="transfer_bank_id" />
        <PhotoInput name="transfer" label="Foto bukti transfer" photos={photos} />
      </div>
      <div id="pmTempo" className={`stack-sm${st.method === 'tempo' ? '' : ' hidden'}`}>
        {drv ? (
          <div className="note info small">{`Jatuh tempo otomatis mengikuti termin toko (${n.term_days || settingNum(meta.settings, 'default_term_days')} hari).`}</div>
        ) : (
          <label className="f">
            Jatuh tempo
            <input
              autoComplete="off"
              className="inp"
              type="date"
              name="due_date"
              defaultValue={addDays(today(), n.term_days || 7)}
            />
          </label>
        )}
      </div>
      <PhotoInput name="proof" label="Foto nota bertanda tangan (opsional)" photos={photos} />
    </>
  );
}
